import { useState, type JSX } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { MAX_MESSAGE_CHARS } from '@shared/support-bundles/contract'
import type { SubmitResult } from './submit'

export interface SendBundleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSend: (
    message: string,
    options: { includeScreenshot: boolean; includeModel: boolean },
  ) => Promise<SubmitResult>
  /** Whether a model is currently open; there is nothing to opt out of if not. */
  hasModel: boolean
}

/**
 * Renders a wait as the user should read it.
 *
 * Deliberately not rounded to hours: `Math.round(1800 / 3600)` is 1, so a
 * half-hour wait would read "about 1 hour" and send the user back twice as late
 * as they need to.
 */
function describeWait(seconds: number | undefined): string {
  if (seconds === undefined) return 'Please try again later.'
  if (seconds < 60) return `Please try again in ${seconds} seconds.`
  const minutes = Math.ceil(seconds / 60)
  return `Please try again in ${minutes} minutes.`
}

/**
 * One sentence for every way our side can fail to deliver a bundle.
 *
 * Shared rather than duplicated so the two branches cannot drift into implying
 * a difference the user has no way to act on.
 */
const UNAVAILABLE_MESSAGE = "We can't accept diagnostics right now. Please try again later."

/**
 * Folds the two boxes into the one `message` the wire contract carries.
 *
 * Deliberately NOT a new multipart field. Adding one means a coordinated change
 * with the receiver team, a new part name, and a revised part count — the last
 * of which already caused an outage when a limit was set to the count itself.
 * A labelled block of text conveys the same thing to whoever reads the Bundle,
 * and costs nothing on either side.
 *
 * The heading appears only when there is something under it, so a one-line
 * compliment does not arrive trailing an empty "Expected:".
 */
const EXPECTED_HEADING = '\n\nExpected:\n'

/**
 * Characters reserved for the expectation out of the shared message budget.
 *
 * The two boxes share ONE wire field, and the backend truncates it with
 * `.slice(0, MAX_MESSAGE_CHARS)` — from the start, so the trailing `Expected:`
 * block is precisely what gets cut. Two independently-capped 10,000-character
 * boxes could therefore send 20,012 characters and have the entire expectation
 * silently discarded, defeating the reason the field was added.
 *
 * Splitting the budget means the browser stops you typing rather than the
 * server quietly dropping what you wrote. 2,000 characters is several
 * paragraphs for a field whose useful answers are usually one sentence.
 */
export const EXPECTED_CHARS = 2_000

/** What is left for the description once the expectation and heading are reserved. */
export const MESSAGE_CHARS = MAX_MESSAGE_CHARS - EXPECTED_CHARS - EXPECTED_HEADING.length

export function composeMessage(message: string, expected: string): string {
  const trimmed = expected.trim()
  return trimmed === '' ? message : `${message}${EXPECTED_HEADING}${trimmed}`
}

function errorFor(result: SubmitResult): string {
  switch (result.outcome) {
    case 'rate_limited':
      return `You've sent diagnostics several times recently. ${describeWait(result.retryAfterSeconds)}`
    case 'unavailable':
      return UNAVAILABLE_MESSAGE
    case 'too_large':
      return 'These diagnostics are too large to send. Try again without the model.'
    case 'rejected':
      return "Those diagnostics couldn't be accepted. Please contact support directly."
    case 'offline':
      // The ONLY branch where the user's own network is implicated, and so the
      // only one allowed to mention it.
      return "We couldn't reach the server. Check your connection and try again."
    default:
      // Same sentence as `unavailable`, deliberately.
      //
      // Every remaining case is our service failing to deliver the bundle —
      // a 502 because we could not reach the receiver, a 500, an unrecognised
      // status. The user cannot tell those apart, cannot act on the
      // difference, and should not be asked to.
      //
      // The copy this replaced said "check your connection", which sent a
      // reporter to look at their wifi for an outage inside our VPC — the one
      // component already cleared by the fact that their request reached us
      // and came back. From where they sit this is simply the diagnostics
      // service being unavailable, so it says that.
      //
      // The distinction between these causes is real and it is preserved where
      // it is actionable: in the backend's log and response, which name the
      // credential, the transport and the receiver separately for whoever is
      // on call.
      return UNAVAILABLE_MESSAGE
  }
}

/**
 * The Send feedback dialog.
 *
 * **Feedback** is what the person does; **diagnostics** is what gets sent. That
 * split is why this can be called feedback at all — CONTEXT.md used to forbid
 * the word outright, on a list whose one stated reason was the collision with
 * Report (the PDF an Engineer produces from a Model). That reason applies to
 * "report" and never applied to "feedback".
 *
 * Still never "report", for exactly that collision.
 *
 * The copy invites more than faults. "Tell us what went wrong" is what the
 * dialog used to say, and it suppresses both praise and suggestions by
 * presuming something broke. The cost of widening it is that someone will type
 * "love the new icons" and ship their whole Model with it — which is why the
 * consent paragraph below stays as blunt as it is, and why the artifact
 * checkboxes sit directly above it.
 *
 * TWO fields now, where the rule was one. The original rule — no category,
 * severity, title or contact field, because every extra field stands between a
 * frustrated Engineer and a filed Bundle — is deliberately reversed for
 * expectation alone, and only as an OPTIONAL field. What happened survives in
 * the logs, the screenshot and the Model. What the Engineer expected exists
 * only in their head, and they have moved on by the time triage asks.
 *
 * On failure the dialog stays open with both drafts intact. Retyping something
 * they already wrote is the fastest way to make someone stop telling you.
 */
export function SendBundleDialog(props: SendBundleDialogProps): JSX.Element {
  const { open, onOpenChange, onSend, hasModel } = props
  const [message, setMessage] = useState('')
  const [expected, setExpected] = useState('')
  const [includeScreenshot, setIncludeScreenshot] = useState(true)
  const [includeModel, setIncludeModel] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = async (): Promise<void> => {
    if (sending || message.trim() === '') return
    setSending(true)
    setError(null)

    const result = await onSend(composeMessage(message, expected), {
      includeScreenshot,
      includeModel: hasModel && includeModel,
    })

    setSending(false)
    if (result.outcome === 'accepted') {
      toast.success(`Thanks — reference ${result.reference}`, {
        description: 'Quote this reference if you follow up with support.',
      })
      setMessage('')
      // Cleared only on success, alongside the message. A failed send keeps
      // both drafts: retyping something you already wrote is the fastest way
      // to make someone stop telling you.
      setExpected('')
      onOpenChange(false)
      return
    }
    setError(errorFor(result))
  }

  return (
    <Dialog
      open={open}
      // A send in flight owns the dialog: closing mid-request would discard the
      // draft while the request is still deciding whether it needs it.
      onOpenChange={(next): void => {
        if (!sending) onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what&apos;s working, what isn&apos;t, and anything you&apos;d change.
            We&apos;ll attach the technical details so you don&apos;t have to describe them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label htmlFor="bundle-message">Your feedback</Label>
          <Textarea
            id="bundle-message"
            rows={5}
            maxLength={MESSAGE_CHARS}
            disabled={sending}
            placeholder="What were you doing, and what did you see?"
            value={message}
            onChange={(e): void => setMessage(e.target.value)}
          />
        </div>

        {/* Optional, and that is the whole reason it is allowed to exist.
            This component's original rule was one free-text box and no other
            field, because every extra field stands between a frustrated
            Engineer and a filed Bundle. That rule is reversed here for one
            thing only: expectation is what triage cannot reconstruct later.
            What happened survives in the logs, the screenshot and the Model;
            what the Engineer THOUGHT would happen exists only in their head,
            and they have moved on by the time anyone asks. Keeping it optional
            preserves the original intent — one line and Send still works. */}
        <div className="space-y-1">
          <Label htmlFor="bundle-expected">
            What did you expect to happen?{' '}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Textarea
            id="bundle-expected"
            rows={3}
            maxLength={EXPECTED_CHARS}
            disabled={sending}
            placeholder="Often the quickest way for us to spot the difference."
            value={expected}
            onChange={(e): void => setExpected(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="bundle-screenshot"
              disabled={sending}
              checked={includeScreenshot}
              onCheckedChange={(v): void => setIncludeScreenshot(v === true)}
            />
            <Label htmlFor="bundle-screenshot">Include a screenshot</Label>
          </div>
          {/* Nothing to opt out of with no model open, and a dead checkbox
              invites a reader to think a model was deliberately withheld. */}
          {hasModel && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="bundle-model"
                disabled={sending}
                checked={includeModel}
                onCheckedChange={(v): void => setIncludeModel(v === true)}
              />
              <Label htmlFor="bundle-model">Include my model</Label>
            </div>
          )}
        </div>

        {/* This copy carries the consent for everything the bundle collects.
            Written to be understood rather than to be defensible: "app
            metadata" is accurate to an engineer and meaningless to a customer,
            and it does not convey that the model itself is being sent. */}
        <p data-testid="bundle-consent" className="text-xs text-muted-foreground">
          This sends to Revalize: your open model including its results and a screenshot of the
          app (you can turn either off above), recent application logs, and your account and
          browser details. Your name and email come from your sign-in. We delete them once
          we&apos;ve finished reviewing them, or when they&apos;re attached to a support ticket
          we&apos;ve opened for you.
        </p>

        {error !== null && (
          <p data-testid="bundle-error" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={sending} onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={sending || message.trim() === ''} onClick={(): void => void handleSend()}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
